import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'universal-cookie';
import NotifyService from 'services/base/notify.service';
import {
	MAX_AGE_MS,
	PAGE_SIZE,
	isOlderThan,
	mergeByMessageId,
	normalizeNotification,
} from 'helpers/notifications/notificationPayload';
import { groupNotificationsByTime } from 'helpers/notifications/notificationTimeGroups';

const cookies = new Cookies();

/**
 * Notification center state: paging, badge, filter, realtime, underflow fill.
 */
export default function useNotificationCenter() {
	const alerts = useMemo(() => NotifyService.getInstance(), []);
	const timeZone = cookies.get('timeZone') || 'UTC';

	const [isOpen, setIsOpen] = useState(false);
	const [onlyUnread, setOnlyUnread] = useState(true);
	const [notifications, setNotifications] = useState([]);
	const [badgeCount, setBadgeCount] = useState(0);
	const [isInitialLoading, setIsInitialLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);

	const currentPageRef = useRef(0);
	const fetchedCountRef = useRef(0);
	const requestGenerationRef = useRef(0);
	const viewportRef = useRef(null);
	const fillTimerRef = useRef(undefined);
	const loadNextPageRef = useRef(() => {});
	const notificationsRef = useRef(notifications);
	const onlyUnreadRef = useRef(onlyUnread);
	const isOpenRef = useRef(isOpen);
	const hasMoreRef = useRef(hasMore);
	const badgeCountRef = useRef(badgeCount);
	const isInitialLoadingRef = useRef(isInitialLoading);
	const isLoadingMoreRef = useRef(isLoadingMore);

	notificationsRef.current = notifications;
	onlyUnreadRef.current = onlyUnread;
	isOpenRef.current = isOpen;
	hasMoreRef.current = hasMore;
	badgeCountRef.current = badgeCount;
	isInitialLoadingRef.current = isInitialLoading;
	isLoadingMoreRef.current = isLoadingMore;

	const visibleNotifications = useMemo(
		() => (onlyUnread ? notifications.filter((n) => !n.isRead) : notifications),
		[notifications, onlyUnread],
	);

	const groupedNotifications = useMemo(
		() => groupNotificationsByTime(visibleNotifications, timeZone),
		[visibleNotifications, timeZone],
	);

	const hasReachedEnd =
		!isInitialLoading && !isLoadingMore && !hasMore && !loadFailed;

	const canMarkAsRead = notifications.some((n) => !n.isRead);
	const canClear = notifications.length > 0;

	const scheduleUnderflowCheck = useCallback(() => {
		if (fillTimerRef.current) {
			clearTimeout(fillTimerRef.current);
		}

		fillTimerRef.current = setTimeout(() => {
			const viewport = viewportRef.current;

			if (
				!isOpenRef.current ||
				!hasMoreRef.current ||
				isInitialLoadingRef.current ||
				isLoadingMoreRef.current ||
				!viewport ||
				viewport.scrollHeight > viewport.clientHeight
			) {
				return;
			}

			// Avoid cascading through the entire inbox when there are no unread items.
			if (onlyUnreadRef.current && badgeCountRef.current === 0) {
				return;
			}

			loadNextPageRef.current();
		});
	}, []);

	const loadPage = useCallback(
		async (page, generation, initial) => {
			if (initial) {
				setIsInitialLoading(true);
			} else {
				setIsLoadingMore(true);
			}
			setLoadFailed(false);

			try {
				const fetchPage = initial ? alerts.getPageRetry.bind(alerts) : alerts.getPage.bind(alerts);
				const { items, totalCount, unreadCount } = await fetchPage({
					page,
					pageSize: PAGE_SIZE,
				});

				if (generation !== requestGenerationRef.current) {
					return;
				}

				const cutoff = Date.now() - MAX_AGE_MS;
				const reachedCutoff = items.some((notification) => isOlderThan(notification, cutoff));
				const recentItems = items.filter((notification) => !isOlderThan(notification, cutoff));

				currentPageRef.current = page;
				fetchedCountRef.current += items.length;
				setBadgeCount(unreadCount);
				setNotifications((current) => mergeByMessageId(current, recentItems));
				setHasMore(
					!reachedCutoff &&
						items.length === PAGE_SIZE &&
						fetchedCountRef.current < totalCount,
				);
				scheduleUnderflowCheck();
			} catch {
				if (generation === requestGenerationRef.current) {
					setLoadFailed(true);
				}
			} finally {
				if (generation === requestGenerationRef.current) {
					setIsInitialLoading(false);
					setIsLoadingMore(false);
				}
			}
		},
		[alerts, scheduleUnderflowCheck],
	);

	const refresh = useCallback(() => {
		const generation = ++requestGenerationRef.current;
		currentPageRef.current = 0;
		fetchedCountRef.current = 0;
		setNotifications([]);
		setHasMore(true);
		setLoadFailed(false);
		return loadPage(1, generation, true);
	}, [loadPage]);

	const loadNextPage = useCallback(() => {
		if (isInitialLoadingRef.current || isLoadingMoreRef.current || !hasMoreRef.current) {
			return;
		}
		loadPage(currentPageRef.current + 1, requestGenerationRef.current, false);
	}, [loadPage]);

	loadNextPageRef.current = loadNextPage;

	const onScroll = useCallback(
		(event) => {
			const viewport = event.target;
			if (viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 32) {
				loadNextPage();
			}
		},
		[loadNextPage],
	);

	const retry = useCallback(() => {
		if (currentPageRef.current === 0) {
			refresh();
			return;
		}
		loadPage(currentPageRef.current + 1, requestGenerationRef.current, false);
	}, [loadPage, refresh]);

	const markAsRead = useCallback(async () => {
		if (!notificationsRef.current.some((n) => !n.isRead)) {
			return;
		}

		setBadgeCount(0);
		setNotifications((current) => current.map((n) => ({ ...n, isRead: true })));
		setIsOpen(false);

		try {
			await alerts.markAsRead();
			await refresh();
		} catch {
			// Toast already shown by NotifyService; keep optimistic UI.
		}
	}, [alerts, refresh]);

	const clearAll = useCallback(async () => {
		if (notificationsRef.current.length === 0) {
			return;
		}

		setBadgeCount(0);
		setNotifications([]);
		setIsOpen(false);

		try {
			await alerts.clearAll();
			await refresh();
		} catch {
			// Toast already shown by NotifyService.
		}
	}, [alerts, refresh]);

	const onOpenChange = useCallback(
		(open) => {
			setIsOpen(open);
			if (open) {
				setOnlyUnread(true);
				scheduleUnderflowCheck();
			}
		},
		[scheduleUnderflowCheck],
	);

	const onUnreadFilterChange = useCallback(
		(checked) => {
			setOnlyUnread(checked);
			scheduleUnderflowCheck();
		},
		[scheduleUnderflowCheck],
	);

	useEffect(() => {
		refresh();
	}, [refresh]);

	useEffect(() => {
		const handleNewNotification = (e) => {
			if (e.detail?.reset) {
				setNotifications([]);
				setBadgeCount(0);
				return;
			}

			const newData = e.detail?.data;
			if (!newData || newData.title === 'Parameter optimization progress') {
				return;
			}

			if (newData.isError) {
				alerts.notifications.error(newData.title, newData.text);
			} else if (newData.isWarning) {
				alerts.notifications.warning(newData.title, newData.text);
			} else if (newData.isInfo) {
				alerts.notifications.info(newData.title, newData.text);
			} else {
				alerts.notifications.success(newData.title, newData.text);
			}

			if (newData.onlyNotify) {
				return;
			}

			const notification = normalizeNotification(newData);
			if (!notification.messageId) {
				return;
			}

			setNotifications((current) => {
				const alreadyPresent = current.some((existing) => existing.messageId === notification.messageId);
				const next = mergeByMessageId(current, [notification], true);
				if (!alreadyPresent && !notification.isRead) {
					setBadgeCount((count) => count + 1);
				}
				return next;
			});
		};

		alerts.subscribe(handleNewNotification);
		return () => alerts.unsubscribe(handleNewNotification);
	}, [alerts]);

	useEffect(
		() => () => {
			if (fillTimerRef.current) {
				clearTimeout(fillTimerRef.current);
			}
		},
		[],
	);

	return {
		timeZone,
		isOpen,
		onlyUnread,
		notifications,
		visibleNotifications,
		groupedNotifications,
		badgeCount,
		isInitialLoading,
		isLoadingMore,
		hasMore,
		loadFailed,
		hasReachedEnd,
		canMarkAsRead,
		canClear,
		viewportRef,
		onOpenChange,
		onUnreadFilterChange,
		onScroll,
		retry,
		markAsRead,
		clearAll,
	};
}
